/**
 * THE EXAM CREATION TOOL — how a teacher with no exam gets one, and how one with an exam already ADJUSTS
 * it afterwards without starting over.
 *
 * The division of labour between the two agents is the whole design here, and it is enforced by the tool
 * description rather than by a state machine:
 *
 *   THE MAIN AGENT talks to the teacher. It collects the metadata (subject, topic, class, working time,
 *   points) and whatever else the teacher wants, then develops a ROUGH plan in the chat — which tasks, of
 *   which kind, worth how much — and gets it explicitly confirmed. That conversation is the only place
 *   the teacher's intent exists, so it cannot be skipped or guessed at.
 *
 *   THE CREATION AGENT refines that rough plan internally: exact wording, sub-tasks, answer space,
 *   figures, and the document itself. It gets minutes and several model calls to do it, which is why it
 *   is a job and not part of the chat turn.
 *
 * WHY THE CONFIRMATION IS A PARAMETER AND NOT JUST AN INSTRUCTION. A creation run costs real money and
 * produces a printable artifact the teacher is meant to hand out, so "the plan was confirmed" is a fact
 * the tool needs, not a hope. The handler refuses without it and tells the model what to do instead —
 * which turns a skipped conversation into a recoverable turn rather than a wrong exam.
 *
 * ANPASSUNG (refinement) IS A DIFFERENT PATH THROUGH THE SAME TOOL, not a second one — the teacher's
 * question ("can I get a version of this exam without an exam already on it?" vs. "can task 3 be worth
 * more?") is the same question of degree, and the job (`examCreation`) already has a `refinementInstruction`
 * field that runs the creation agent's Delta Change Planner against the exam's own current source instead
 * of writing a fresh one (see `loadRefinementContext`/`requestChangePlan` in exam-creation-agent.ts). It
 * needs no rough plan or confirmation gate of its own: the instruction IS the ask, in the teacher's words,
 * the same way `feedback` is for the master-solution/grading-scheme refine tools.
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam, type ExamState } from '../../exam.js';
import { ALL_SUBJECTS, normalizeSubject } from '../../subjects.js';
import { isExamCreatorLocked } from '../../utils/exam-lock.js';
import { dispatchJobs } from '../jobs/dispatch.js';
import type { ExamCreationPayload } from '../jobs/types/exam-creation.job.js';

export const createExamToolDef = [
  {
    type: 'function',
    function: {
      name: 'create_exam',
      description:
        'Erstellt eine komplette, ausdruckbare Klausur (Aufgabenblatt inklusive Abbildungen) und legt sie im System als fertige Klausur an — mit Aufgaben, Punkten, Aufgabentypen und PDF. ODER passt eine bereits erstellte Klausur an (siehe "anpassungsanweisung"). Nutze dieses Tool nur, wenn der User eine NEUE Klausur erstellen lassen will und noch keine Klausur hochgeladen hat, oder wenn er eine bereits ERSTELLTE (nicht hochgeladene) Klausur ändern will.\n\n' +
        'NEUE KLAUSUR — PFLICHTABLAUF VOR DEM TOOLCALL — in dieser Reihenfolge:\n' +
        '1) METADATEN SAMMELN: Fach, Thema, Klasse/Jahrgang und Bearbeitungszeit sind zwingend. Frage danach, was noch fehlt, und frage in EINER Nachricht (nicht einzeln nacheinander). Kläre außerdem, ob der User besondere Wünsche hat (Schwerpunkte, ausgeschlossene Inhalte, Schwierigkeitsgrad, gewünschte Aufgabentypen)\n' +
        '2) Groben PLAN MIT DEM USER ENTWICKELN: Schlage im Chat eine GROBE Aufgabenübersicht vor — welche Aufgaben, welcher Aufgabentyp, welches Material. Grob heißt: eine Zeile pro Aufgabe, KEINE ausformulierten Aufgabentexte. Die Feinausarbeitung (Formulierungen, Teilaufgaben, Antwortplatz, Abbildungen, Punkteverteilung) macht die Klausurerstellung anschließend selbst.\n' +
        'Falls der Nutzer nach Punkten fragt, teile ihm mit, dass Punkte nach der Erstellung der Aufgaben festgelegt werden und bearbeitet werden können.\n' +
        '3) BESTÄTIGUNG EINHOLEN: Frage den User ausdrücklich, ob der plan so passt, und übernimm seine Änderungswünsche. Erst wenn er zustimmt, rufe dieses Tool mit plan_bestaetigt=true auf und übergib den bestätigten plan im Feld grobplan.\n' +
        'Rufe das Tool NIEMALS ohne bestätigten plan auf.\n\n' +
        'Vor diesem Tool solltest du die Klausurmetadaten setzen set_metadata.\n' +
        'ABLAUF: Planen -> Bestätigen -> set_metadata -> create_exam. Niemals andersherum.\n\n' +
        'BESTEHENDE KLAUSUR ANPASSEN — ein anderer Ablauf, kein plan/keine Bestätigung nötig: wenn der User eine Änderung an einer bereits ERSTELLTEN Klausur wünscht ("Aufgabe 3 soll mehr Punkte geben", "füg noch eine Aufgabe zu X hinzu", "die Abbildung bei Aufgabe 2 passt nicht"), fülle NUR "anpassungsanweisung" mit der Änderung in den Worten des Users aus (die anderen Felder weglassen oder — falls bekannt — informativ mitgeben) und rufe das Tool auf. Frage vorher kurz nach, was genau geändert werden soll, falls das nicht klar ist, aber entwickle KEINEN neuen plan und verlange KEINE Bestätigung dafür — das ist nur für eine komplett neue Klausur nötig.\n\n' +
        'Beide Fälle laufen danach mehrere Minuten im Hintergrund.' +
        'Frühere Versionen der Klausur werden nicht gespeichert. Teile das dem Nutzer mit, falls er nach einer solchen Anpassung fragt. Er muss dann die frühere Version beschreiben, damit sie wiederhergestellt werden kann.',
      parameters: {
        type: 'object',
        properties: {
          fach: {
            type: 'string',
            enum: [...ALL_SUBJECTS],
            description:
              'Standardisierter Fach-Key (z.B. "math", "biology"). Falls keiner passt: "other". Bei einer Anpassung optional.',
          },
          thema: {
            type: 'string',
            description: 'Thema der Klausur, z.B. "Das menschliche Herz". Bei einer Anpassung optional.',
          },
          klasse: {
            type: 'string',
            description: 'Klasse/Jahrgang, z.B. "10b" — bestimmt das Niveau. Bei einer Anpassung optional.',
          },
          bearbeitungszeit_minuten: {
            type: 'number',
            description: 'Bearbeitungszeit in Minuten. Bei einer Anpassung optional.',
          },
          gesamtpunkte: {
            type: 'number',
            description: 'Gewünschte Gesamtpunktzahl. Weglassen, wenn der User keine nennt.',
          },
          grobplan: {
            type: 'string',
            description:
              'NUR für eine NEUE Klausur: der mit dem User abgestimmte GROBPLAN, so wie er bestätigt wurde: eine Zeile pro Aufgabe mit Aufgabentyp und Inhalt. Keine ausformulierten Aufgabentexte. Bei einer Anpassung (anpassungsanweisung gesetzt) weglassen.',
          },
          plan_bestaetigt: {
            type: 'boolean',
            description:
              'NUR für eine NEUE Klausur: nur true, wenn der User den Grobplan im Chat ausdrücklich bestätigt hat. Ohne Bestätigung wird das Tool abgelehnt. Bei einer Anpassung ohne Bedeutung.',
          },
          anpassungsanweisung: {
            type: 'string',
            description:
              'NUR zum Anpassen einer bereits ERSTELLTEN Klausur (nicht für eine neue): die gewünschte Änderung in den Worten des Users, z.B. "Aufgabe 3 auf 8 Punkte setzen und eine Teilaufgabe zur Ableitung ergänzen". Wenn gesetzt, wird die Klausur GEZIELT angepasst statt neu geschrieben — bestehende Aufgaben und Abbildungen, die nicht betroffen sind, bleiben unverändert. Nicht zusammen mit "ersetzen" nutzen.',
          },
          zusatzhinweise: {
            type: 'string',
            description:
              'Weitere Wünsche des Users im Wortlaut: Schwerpunkte, ausgeschlossene Inhalte, Schwierigkeitsgrad, gewünschte oder unerwünschte Aufgabentypen, Hinweise für die Schüler.',
          },
          sprache: { type: 'string', description: 'Sprache der Klausur, falls abweichend von der Chatsprache' },
          datum: { type: 'string', description: 'Datum, das auf der Klausur stehen soll' },
          lehrkraft: { type: 'string', description: 'Name der Lehrkraft für den Kopf der Klausur' },
          schule: { type: 'string', description: 'Name der Schule für den Kopf der Klausur' },
          ersetzen: {
            type: 'boolean',
            description:
              'Nur true, wenn die Klausur bereits Aufgaben hat, komplett NEU geschrieben werden soll (nicht nur angepasst), und der User das ausdrücklich bestätigt hat. Nicht zusammen mit "anpassungsanweisung" nutzen — für eine gezielte Änderung genügt diese allein.',
          },
        },
        required: [],
      },
    },
  },
];

export async function handleCreateExam(exam: Exam, toolCallArgs: any): Promise<object> {
  const examId = exam?._id;
  if (!examId) return { content: 'Fehler: Keine Klausur-ID vorhanden.' };

  const examCollection = await getCollection(Exam);
  const currentExam = await examCollection.findOne({ _id: examId });
  if (!currentExam) return { content: 'Fehler: Klausur nicht gefunden.' };

  // Both of the job's own `ready` gates, asked here too — so the teacher gets an explanation in the chat
  // instead of a row that is dispatched and then silently dropped. Applies to a refinement exactly like a
  // fresh create/replace: either one rewrites `exam.tasks` wholesale, and student answers bind to a task by
  // number, so either one can silently detach an answer already extracted.
  if ((currentExam.examState?.uploadProgress?.submissionsFound ?? 0) > 0) {
    return {
      content:
        'SYSTEM INFO: Für diese Klausur liegen bereits Schülerabgaben vor. Eine Erstellung oder Anpassung kann hier ' +
        'nicht laufen, weil die Abgaben den bestehenden Aufgaben zugeordnet sind. Erkläre dem User, dass er dafür ' +
        'eine neue, leere Klausur anlegen muss.',
    };
  }

  // Same reasoning, extended to solution content: once a master solution or grading scheme has been
  // uploaded or generated for this exam, the tree is what THAT hangs off too — see `isExamCreatorLocked`,
  // the one definition this check, the job's own `ready()`, and `createExamAvailability` below all share.
  // Checked here as well, not just left to the job's drop, so the teacher hears a real answer this turn
  // instead of a background row that silently vanishes — and this tool should not normally even be
  // offered once locked (see `createExamAvailability`), so reaching this branch means the model tried it
  // anyway.
  if (isExamCreatorLocked(currentExam)) {
    return {
      content:
        'SYSTEM INFO: Für diese Klausur liegt bereits eine Musterlösung oder ein Bewertungsschema vor (hochgeladen ' +
        'oder erstellt). Diese Klausur ist deshalb gesperrt: Struktur, Aufgaben und Punkte können nicht mehr per KI ' +
        'erstellt oder angepasst werden, weil Musterlösung/Bewertungsschema an die Aufgaben gebunden sind und dabei ' +
        'verloren gehen würden. Erkläre dem User klar, dass diese Klausur nicht mehr bearbeitbar ist, und biete ' +
        'KEINE Änderung der Klausurstruktur mehr an.',
    };
  }

  const hasTasks = Array.isArray(currentExam.tasks) && currentExam.tasks.length > 0;
  const anpassungsanweisung = optionalString(toolCallArgs?.anpassungsanweisung);

  // ── ANPASSEN: a targeted patch onto the exam that is already there, no rough plan or confirmation of
  // one needed — the instruction itself is the confirmed ask, same as `feedback` on the master-solution
  // and grading-scheme refine tools.
  if (anpassungsanweisung && toolCallArgs?.ersetzen !== true) {
    if (!hasTasks) {
      return {
        content:
          'SYSTEM INFO: Diese Klausur hat noch keine Aufgaben, es gibt also nichts anzupassen. Rufe das Tool ' +
          'stattdessen für eine NEUE Klausur auf (Metadaten sammeln, Grobplan abstimmen, dann grobplan + ' +
          'plan_bestaetigt=true übergeben — ohne anpassungsanweisung).',
      };
    }

    const payload: ExamCreationPayload = {
      subject: normalizeSubject(toolCallArgs?.fach),
      topic: optionalString(toolCallArgs?.thema),
      schoolClass: optionalString(toolCallArgs?.klasse),
      durationMinutes: Number(toolCallArgs?.bearbeitungszeit_minuten) || undefined,
      totalPoints: Number(toolCallArgs?.gesamtpunkte) || undefined,
      date: optionalString(toolCallArgs?.datum),
      teacher: optionalString(toolCallArgs?.lehrkraft),
      school: optionalString(toolCallArgs?.schule),
      language: optionalString(toolCallArgs?.sprache),
      teacherNotes: optionalString(toolCallArgs?.zusatzhinweise),
      refinementInstruction: anpassungsanweisung,
    };

    const { jobIds } = await dispatchJobs('examCreation', examId, payload);
    if (jobIds.length === 0) {
      logger.warn({ examId }, 'Exam refinement dispatch produced no job');
      return {
        content:
          'SYSTEM INFO: Die Anpassung konnte nicht gestartet werden. Sage dem User, dass etwas schiefgelaufen ist, ' +
          'und schlage vor, es erneut zu versuchen.',
      };
    }

    logger.info({ examId, jobIds }, 'Exam refinement dispatched');
    // NO `canvasToOpen` HERE ON PURPOSE. Returning one writes `pendingCanvasOpen` onto the conversation
    // immediately (see agent-loop.ts's `finalizeAgentTurn`), which opens the canvas right away and hides
    // the progress sidebar for the run's whole duration (`StepsIconSidebar` only renders while no canvas is
    // open, chat.tsx) — exactly the bug where the ring never appeared and the canvas looked like it "opened
    // once generation finished". The job's own completion write already reopens it for real once it's done
    // (see exam-creation.job.ts) — that is the only place this should open from.
    return {
      content:
        'SYSTEM INFO: Die Klausur wird jetzt im Hintergrund angepasst. Das dauert einige Minuten. Sage dem User, ' +
        'dass er in der Zwischenzeit nichts tun muss, dass der Fortschritt in der Statusleiste sichtbar ist, ' +
        'und dass du dich von selbst meldest, sobald die Anpassung fertig ist. Kündige nichts an, was noch nicht fertig ist.',
    };
  }

  // ── NEU ERSTELLEN (oder komplett ersetzen): the confirmation gate. Refused with an instruction rather
  // than an error: the model skipped a step of the conversation, and the recovery is to have that
  // conversation, not to retry the call.
  if (toolCallArgs?.plan_bestaetigt !== true) {
    return {
      content:
        'SYSTEM INFO: Die Klausurerstellung wurde NICHT gestartet, weil der Plan noch nicht bestätigt ist. ' +
        'Stelle dem User jetzt den Plan im Chat vor (eine Zeile pro Aufgabe mit Aufgabentyp und Punkten), ' +
        'frage ausdrücklich, ob er so passt, und rufe das Tool erst nach seiner Zustimmung erneut auf.',
    };
  }

  const grobplan = optionalString(toolCallArgs?.grobplan) ?? '';
  if (!grobplan) {
    return {
      content:
        'SYSTEM INFO: Die Klausurerstellung wurde NICHT gestartet, weil kein Plan übergeben wurde. ' +
        'Übergib den mit dem User abgestimmten Plan im Feld grobplan.',
    };
  }

  if (hasTasks && toolCallArgs?.ersetzen !== true) {
    return {
      content:
        'SYSTEM INFO: Diese Klausur hat bereits Aufgaben. Wenn nur ein Teil geändert werden soll, rufe das Tool ' +
        'stattdessen mit "anpassungsanweisung" auf (keine Bestätigung nötig). Für eine komplette Neuerstellung: ' +
        'frage den User, ob die bestehenden Aufgaben wirklich ersetzt werden sollen (Musterlösungen und ' +
        'Bewertungsschemata dieser Aufgaben gehen dabei verloren), und rufe das Tool nur bei ausdrücklicher ' +
        'Zustimmung erneut auf — dann mit ersetzen=true.',
    };
  }

  const payload: ExamCreationPayload = {
    subject: normalizeSubject(toolCallArgs?.fach),
    topic: optionalString(toolCallArgs?.thema),
    schoolClass: optionalString(toolCallArgs?.klasse),
    durationMinutes: Number(toolCallArgs?.bearbeitungszeit_minuten) || undefined,
    totalPoints: Number(toolCallArgs?.gesamtpunkte) || undefined,
    date: optionalString(toolCallArgs?.datum),
    teacher: optionalString(toolCallArgs?.lehrkraft),
    school: optionalString(toolCallArgs?.schule),
    language: optionalString(toolCallArgs?.sprache),
    // The confirmed rough plan and the teacher's own words travel together: the creation agent refines
    // the plan, and the notes are the constraints it must refine WITHIN.
    confirmedPlan: grobplan,
    teacherNotes: optionalString(toolCallArgs?.zusatzhinweise),
    replaceExisting: toolCallArgs?.ersetzen === true,
  };

  const { jobIds } = await dispatchJobs('examCreation', examId, payload);
  if (jobIds.length === 0) {
    logger.warn({ examId }, 'Exam creation dispatch produced no job');
    return {
      content:
        'SYSTEM INFO: Die Klausurerstellung konnte nicht gestartet werden. Sage dem User, dass etwas schiefgelaufen ' +
        'ist, und schlage vor, es erneut zu versuchen.',
    };
  }

  logger.info({ examId, jobIds }, 'Exam creation dispatched');

  // A PROGRESS BAR NOW, where there used to be none: the job writes its own `examCreationState` (see
  // jobs/types/exam-creation.job.ts), which is exactly what gates the exam-creator step in the sidebar —
  // so pointing the teacher at it is no longer a lie. THE JOB ALSO ANNOUNCES ITSELF into the chat once it
  // lands (success, with any dropped materials/warnings named, or failure after every retry) via a
  // workflow pending task — see exam-creation-finished.task.ts — so "melde dich von selbst" here is a real
  // promise, not a guess.
  //
  // NO `canvasToOpen` HERE ON PURPOSE — see the identical comment on the ANPASSEN branch above. Opening the
  // canvas immediately hid the progress sidebar for the run's whole duration; the job's own completion
  // write is what opens it, once the exam is actually ready.
  return {
    content:
      'SYSTEM INFO: Die Klausur wird jetzt im Hintergrund erstellt — Planung, Abbildungen, Aufgabenblatt. Das dauert ' +
      'einige Minuten. Sage dem User, dass er in der Zwischenzeit nichts tun muss, dass der Fortschritt in der ' +
      'Statusleiste sichtbar ist, und dass du dich von selbst meldest, sobald die Klausur fertig ist — dann sind die ' +
      'Aufgaben im Klausurkontext sichtbar und die Klausur kann als PDF ausgedruckt werden. Kündige nichts an, was ' +
      'noch nicht fertig ist.',
  };
}

/** Only a real string survives: a model can put an object in any of these fields, and `String(...)` on one
 *  would silently print "[object Object]" onto a teacher's exam. */
const optionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  return text.length > 0 ? text : undefined;
};

/**
 * Offered for an exam with no submissions yet — which now includes one that already has tasks, since the
 * tool also covers ANPASSEN (a targeted refinement, via `anpassungsanweisung`), not only a fresh create.
 *
 * STILL NEVER FOR AN UPLOADED EXAM. Creation (and its refinement) and upload are two different origins for
 * the same document, and offering this tool over one the teacher has just uploaded invites the model to
 * overwrite what they gave us instead of working with it.
 *
 * STILL NEVER PAST REAL SUBMISSIONS. Both `create_exam`'s creation/replace path and its refinement path
 * rewrite `exam.tasks` wholesale (see `handleCreateExam`'s own submissions gate, asked again here so the
 * tool is not even offered rather than offered-then-refused) — a submission binds to a task by number, so
 * either one can silently detach an answer already extracted.
 *
 * STILL NEVER ONCE THE EXAM CREATOR IS LOCKED (`isExamCreatorLocked`) — a master solution or grading
 * scheme already uploaded or generated is exactly the same "detaches something a rewrite doesn't reconcile"
 * risk as a submission, just for solution content instead of answers. This is what keeps the main agent
 * from ever OFFERING a change it would then have to refuse mid-turn (see `handleCreateExam`'s own check,
 * asked again for the case where the model calls the tool anyway).
 */
export function createExamAvailability(examState: ExamState, exam?: Exam): boolean {
  if ((examState?.uploadProgress?.uploadedFiles ?? 0) > 0) return false;
  if ((examState?.uploadProgress?.submissionsFound ?? 0) > 0) return false;
  return !isExamCreatorLocked(exam);
}
